<template>
  <div class="admin-search-page">
    <div class="page-container">
      <header class="page-header">
        <h1 class="page-title">Admin Panel</h1>
        <nav class="admin-nav">
          <router-link to="/admin/users" class="nav-tab active">Users</router-link>
          <router-link to="/admin/feedback" class="nav-tab">Feedback</router-link>
          <router-link to="/admin/chapter-roasts" class="nav-tab">Chapter Roasts</router-link>
          <router-link to="/admin/consults" class="nav-tab">Consults</router-link>
        </nav>
      </header>

      <div class="search-section">
        <input
          v-model="searchQuery"
          @input="handleSearch"
          type="text"
          class="search-input"
          placeholder="Search by name or email..."
          autocomplete="off"
        />

        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
        </div>

        <div v-else-if="searchQuery.length >= 2 && users.length === 0" class="empty-state">No users found</div>

        <div v-else-if="users.length > 0" class="users-list">
          <div v-for="user in users" :key="user.uid" class="user-card" @click="router.push(`/admin/users/${user.uid}`)">
            <div class="user-info">
              <div class="user-name">{{ user.name || 'No name' }}</div>
              <div class="user-email">{{ user.email }}</div>
            </div>
            <div class="user-meta">
              <span v-if="user.deletedAt" class="deleted-badge">Deleted</span>
              <span v-else-if="user.lastSeenAt" class="last-seen">{{ formatDate(user.lastSeenAt) }}</span>
            </div>
            <svg class="chevron" viewBox="0 0 20 20" fill="currentColor">
              <path
                fill-rule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
          <p v-if="users.length === 20" class="max-results-note">Showing first 20 results</p>
        </div>
      </div>

      <div class="recent-edits-section">
        <h2 class="section-title">Recent Edits</h2>
        <div v-if="editsLoading" class="loading-state">
          <div class="spinner"></div>
        </div>
        <div v-else-if="recentEdits.length === 0" class="empty-state">No edits yet</div>
        <div v-else class="edits-list">
          <a v-for="edit in recentEdits" :key="edit.id" :href="`/edit/${edit.id}`" target="_blank" class="edit-card">
            <div class="edit-info">
              <div class="edit-title">{{ edit.title || 'Untitled' }}</div>
              <div class="edit-meta">{{ edit.email }} · {{ edit.wordCount.toLocaleString() }} words</div>
            </div>
            <div class="edit-right">
              <span class="edit-status" :class="`status-${edit.status}`">{{ edit.status }}</span>
              <span class="edit-date">{{ formatDate(edit.createdAt) }}</span>
            </div>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '../stores/auth';

interface User {
  uid: string;
  email: string;
  name?: string;
  deletedAt?: string;
  lastSeenAt?: string;
}

interface RecentEdit {
  id: string;
  title: string | null;
  email: string;
  wordCount: number;
  status: string;
  createdAt: string;
}

const router = useRouter();
const authStore = useAuthStore();
const searchQuery = ref('');
const users = ref<User[]>([]);
const loading = ref(false);
const recentEdits = ref<RecentEdit[]>([]);
const editsLoading = ref(true);
let searchTimeout: ReturnType<typeof setTimeout>;

async function handleSearch() {
  clearTimeout(searchTimeout);

  if (searchQuery.value.length < 2) {
    users.value = [];
    return;
  }

  searchTimeout = setTimeout(async () => {
    loading.value = true;
    try {
      const token = await authStore.getIdToken();

      const response = await fetch(`/api/admin/search?q=${encodeURIComponent(searchQuery.value)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        users.value = data.users;
      }
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      loading.value = false;
    }
  }, 500);
}

async function loadRecentEdits() {
  try {
    const token = await authStore.getIdToken();
    const response = await fetch('/api/admin/recent-edits', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.ok) {
      const data = await response.json();
      recentEdits.value = data.projects;
    }
  } catch (error) {
    console.error('Failed to load recent edits:', error);
  } finally {
    editsLoading.value = false;
  }
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

onMounted(loadRecentEdits);
</script>

<style scoped>
.admin-search-page {
  flex: 1;
  background: #f7fafc;
}

.page-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 3rem 1.5rem;
}

.page-header {
  margin-bottom: 2rem;
}

.page-title {
  font-size: 2.25rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 1rem;
}

.admin-nav {
  display: flex;
  gap: 0.25rem;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 0;
}

.nav-tab {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #718096;
  text-decoration: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  transition: color 0.15s;
}

.nav-tab:hover {
  color: #1a202c;
}

.nav-tab.active,
.nav-tab.router-link-active {
  color: var(--color-accent);
  border-bottom-color: var(--color-accent);
}

.search-section {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1.5rem;
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  margin-bottom: 1rem;
  font-family: inherit;
}

.search-input:focus {
  outline: none;
  border-color: var(--color-accent);
  box-shadow: 0 0 0 3px var(--color-accent-light);
}

.loading-state {
  text-align: center;
  padding: 2rem;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e2e8f0;
  border-top-color: var(--color-accent);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.empty-state {
  text-align: center;
  padding: 2rem;
  color: #718096;
}

.users-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.user-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.user-card:hover {
  border-color: #cbd5e0;
  background: #f7fafc;
}

.user-info {
  flex: 1;
}

.user-name {
  font-weight: 600;
  color: #1a202c;
  font-size: 0.875rem;
}

.user-email {
  color: #718096;
  font-size: 0.75rem;
  margin-top: 0.25rem;
}

.user-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.deleted-badge {
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 12px;
  color: #742a2a;
  background: #fed7d7;
}

.last-seen {
  color: #718096;
  font-size: 0.75rem;
}

.chevron {
  width: 20px;
  height: 20px;
  color: #cbd5e0;
  flex-shrink: 0;
}

.max-results-note {
  text-align: center;
  color: #718096;
  font-size: 0.875rem;
  margin-top: 1rem;
}

.recent-edits-section {
  margin-top: 2rem;
}

.section-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1a202c;
  margin-bottom: 1rem;
}

.edits-list {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

.edit-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.875rem 1.25rem;
  border-bottom: 1px solid #e2e8f0;
  text-decoration: none;
  transition: background 0.15s;
}

.edit-card:last-child {
  border-bottom: none;
}

.edit-card:hover {
  background: #f7fafc;
}

.edit-info {
  flex: 1;
  min-width: 0;
}

.edit-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1a202c;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.edit-meta {
  font-size: 0.75rem;
  color: #718096;
  margin-top: 0.125rem;
}

.edit-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
  flex-shrink: 0;
}

.edit-status {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.125rem 0.5rem;
  border-radius: 10px;
  background: #edf2f7;
  color: #4a5568;
}

.edit-status.status-completed {
  background: #c6f6d5;
  color: #22543d;
}

.edit-status.status-processing {
  background: #bee3f8;
  color: #2a4365;
}

.edit-status.status-failed {
  background: #fed7d7;
  color: #742a2a;
}

.edit-date {
  font-size: 0.75rem;
  color: #a0aec0;
}
</style>
